/**
 * English: Station/org manager detail — full station-scoped memory graph and summaries.
 * Thai: รายละเอียดผู้จัดการสถานี — ความจำสถานีและกราฟเหตุการณ์
 */
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useState, useCallback, useRef, useMemo, useEffect } from 'react';
import { ArrowLeftIcon, BuildingOffice2Icon } from '@heroicons/react/24/outline';
import PageShell from '../components/layout/PageShell';
import PageHeader from '../components/layout/PageHeader';
import StatusBadge from '../components/shared/StatusBadge';
import CopyButton from '../components/shared/CopyButton';
import StationMemorySummaryPanel from '../components/agent-management/StationMemorySummaryPanel';
import AgentCompetencyDashboard from '../components/agent-management/AgentCompetencyDashboard';
import KnowledgePanel from '../components/agent-management/knowledge/KnowledgePanel';
import { filterSummariesForDevice } from '../components/agent-management/memoryDeviceRollups';
import { agentManagementQuery } from '../components/agent-management/agentRowView';
import { useStation } from '../context/StationContext';
import { useAgents } from '../hooks/useAgents';
import { useAgentManagementOrgContext } from '../components/agent-management/useAgentManagementOrgContext';
import { useAgentManagementScope } from '../hooks/useAgentManagementScope';
import { useMemorySummaryDaily } from '../hooks/useMemorySummary';

export default function StationManagerDetailPage() {
  const { t } = useTranslation();
  const scope = useAgentManagementScope();
  const { setActiveStationId } = useStation();
  const { manager } = useAgents(scope.stationId);
  const { orgId } = useAgentManagementOrgContext();
  const listHref = `/agent-management${agentManagementQuery(scope.orgName, scope.stationId)}`;

  useEffect(() => {
    if (scope.stationId) {
      setActiveStationId(scope.stationId);
    }
  }, [scope.stationId, setActiveStationId]);

  const activeStation = scope.orgStations.find((s) => s.id === scope.stationId);
  const stationLabel = activeStation?.name || scope.stationId || t('agents.stationManagerNoStation');
  const orgLabel = orgId || scope.orgName || scope.stationId || '—';
  const managerColor = typeof manager?.color === 'string' ? manager.color : '#6366f1';
  const managerName = manager?.name || manager?.id || t('agents.stationManagerNoAgent');
  const managerId = manager?.id ?? '';
  const stationDaily = useMemorySummaryDaily(orgId, scope.stationId, 30, 0, 45);
  const managerDailyItems = useMemo(
    () => filterSummariesForDevice(stationDaily.data?.items ?? [], '', managerId),
    [stationDaily.data, managerId],
  );

  const knowledgeUploadCountRef = useRef(0);
  const [knowledgeRefetchKey, setKnowledgeRefetchKey] = useState(0);
  const handleKnowledgeUpload = useCallback(() => {
    knowledgeUploadCountRef.current += 1;
    setKnowledgeRefetchKey(knowledgeUploadCountRef.current);
  }, []);

  return (
    <PageShell wide>
      <div className="mb-4">
        <Link
          to={listHref}
          className="inline-flex items-center gap-1.5 text-sm text-app-accent hover:underline mb-3"
        >
          <ArrowLeftIcon className="w-4 h-4" />
          {t('agents.stationManagerBack')}
        </Link>
        <PageHeader
          title={t('agents.stationManagerTitle')}
          description={t('agents.stationManagerDetailDescription', {
            manager: managerName,
            station: stationLabel,
          })}
        />
      </div>

      <div className="mx-auto w-full space-y-6">
        <section className="rounded-xl border border-app-border bg-app-surface p-5 shadow-sm">
          <div className="flex items-start gap-3 min-w-0">
            <span
              className="w-2.5 h-2.5 rounded-full shrink-0 mt-1.5 ring-1 ring-hairline"
              style={{ backgroundColor: managerColor }}
              aria-hidden
            />
            <div className="min-w-0 flex-1 space-y-3 text-sm">
              <div className="flex flex-wrap items-center gap-2">
                <h2 className="text-title-sm font-semibold text-ink">{managerName}</h2>
                <span className="inline-flex items-center gap-1 rounded-pill bg-primary/15 px-3 py-1 text-caption-strong font-semibold uppercase tracking-wider text-primary">
                  <BuildingOffice2Icon className="h-3.5 w-3.5 shrink-0" />
                  {t('agents.stationManagerOrg', { org: orgLabel })}
                </span>
                <StatusBadge variant="info" dot>
                  {stationLabel}
                </StatusBadge>
              </div>
              {managerId ? (
                <div className="flex items-center gap-2 min-w-0">
                  <code className="text-xs break-all text-app-text-sec">{managerId}</code>
                  <CopyButton text={managerId} small />
                </div>
              ) : (
                <p className="text-app-text-dim">{t('agents.stationManagerNoAgent')}</p>
              )}
            </div>
          </div>
        </section>

        <AgentCompetencyDashboard
          agentId={managerId || 'station-manager'}
          dailyItems={managerDailyItems}
          loading={stationDaily.loading}
        />

        {orgId && scope.stationId && managerId ? (
          <KnowledgePanel
            orgId={orgId}
            stationId={scope.stationId}
            deviceId={managerId}
            isManager
            deviceColor={managerColor}
            onUploadSuccess={handleKnowledgeUpload}
          />
        ) : null}

        <StationMemorySummaryPanel
          stationScope
          embedded
          knowledgeDeviceId={managerId || undefined}
          knowledgeRefetchKey={knowledgeRefetchKey}
        />
      </div>
    </PageShell>
  );
}
